import { QueryClient, useQuery } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';

import { getCatalogBrowsingType } from '../graphql/catalog-product-browsing-type.2';
import { PdpBrowsingType } from '../graphql/catalog-product-browsing-type_types.2';
import { useCatalogProductIdParams } from '../hooks/useCatalogProductIdParams';
import { queryOptions } from '../lib/queryHelpers';
import { checkValidCatalogProductId } from '../lib/utils';

interface CatalogBrowsingTypeParam {
  catalog_product_id: string;
  browsing_type?: string | null;
}

export const QUERY_KEY = 'getCatalogBrowsingType';

export async function prefetchCatalogBrowsingType(
  params: CatalogBrowsingTypeParam,
  options: GqlRequestOptions,
  query_client: QueryClient,
  topPriorityBrowsingType: string | null,
) {
  const query_options = getCatalogBrowsingTypeQueryOption(params, options, topPriorityBrowsingType);
  await query_client.prefetchQuery(query_options);
  return await query_client.getQueryData(query_options.queryKey);
}

export function getCatalogBrowsingTypeQueryOption(
  params: CatalogBrowsingTypeParam,
  options?: GqlRequestOptions,
  topPriorityBrowsingType?: string | null,
) {
  return {
    queryKey: [QUERY_KEY],
    queryFn: async function () {
      if (topPriorityBrowsingType) {
        return {
          browsingType: topPriorityBrowsingType,
          isNative: topPriorityBrowsingType === PdpBrowsingType.DETAIL_BROWSER,
        };
      }

      // 쿼리파라미터가 존재하지 않을 경우, 쿼리 질의를 통해 브라우징타입을 서버에서 받아온다.
      const { data } = await getCatalogBrowsingType(params, { show_alert: false, ...options });
      const browsingType = data?.pdp_browsing_type?.type ?? PdpBrowsingType.NORMAL;

      return {
        browsingType,
        isNative: browsingType === PdpBrowsingType.DETAIL_BROWSER,
      };
    },
    ...queryOptions,
    enabled: checkValidCatalogProductId(params.catalog_product_id),
  };
}

export function useCatalogBrowsingTypeQuery(options?: GqlRequestOptions) {
  const { catalog_product_id } = useCatalogProductIdParams();
  const queryParams: CatalogBrowsingTypeParam = {
    catalog_product_id,
    browsing_type: null,
  };

  const DEFAULT_DATA = {
    browsingType: PdpBrowsingType.NORMAL,
    isNative: false,
  };

  const response = useQuery(getCatalogBrowsingTypeQueryOption(queryParams, options));
  return {
    ...response,
    ...(response.error || !response.data ? DEFAULT_DATA : response.data),
  };
}
